import type { AxiosResponse } from 'axios';
import type { TaggedUnion } from 'type-fest';
import z from 'zod';

import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import { ChartOfAccountSchema } from '../chart-of-accounts/chartOfAccountsTypes';
import type { QcsResourceSlice } from '../core/resourceSliceUtils';

export const TRANSACTION_RULES_REDUCER_KEY = 'transactionRulesStore';
export type TRANSACTION_RULES_REDUCER_KEY = typeof TRANSACTION_RULES_REDUCER_KEY;

export const EDITING = 'EDITING';
export const VALIDATING = 'VALIDIATING';
export const RESOLVING = 'RESOLVING';
export const VALIDATED = 'VALIDATED';
export const SUBMITTING = 'SUBMITTING';
export const SUBMITTED = 'SUBMITTED';
export const CLEARED = 'CLEARED';

export type TransactionRulesStages =
  | typeof EDITING
  | typeof VALIDATING
  | typeof RESOLVING
  | typeof VALIDATED
  | typeof SUBMITTING
  | typeof SUBMITTED
  | typeof CLEARED;

const usageTypeSchema = z.enum(['PERSONAL', 'BUSINESS']);
const payeeNameTypeSchema = z.enum(['STATEMENT', 'INFERRED']);
const payeeMatchingOperatorSchema = z.enum(['CONTAINS', 'EQUALS']);
const amountMatchingOperatorSchema = z.enum(['EQUALS', 'GREATER_THAN', 'LESS_THAN', 'BETWEEN', 'ANY_AMOUNT']);
const serviceLevel = z.enum(['BASIC', 'PREMIUM']);
const amountTypeSchema = z.enum(['INCOME', 'EXPENSE']).optional();
export const accountTypeSchema = z.enum(['BUSINESS', 'PERSONAL', 'SPECIFIC']);

const payeeMatchingCriteriaSchema = z.object({
  payeeMatchingOperator: payeeMatchingOperatorSchema,
  payeeName: z.string().optional(),
});

const payeeSourceSchema = z.object({
  payeeType: payeeNameTypeSchema,
  payeeMatchingCriteria: z.array(payeeMatchingCriteriaSchema),
});

const accountSourceSchema = z.discriminatedUnion('accountSourceType', [
  z.object({
    accountSourceType: z.enum(['BUSINESS', 'PERSONAL']),
  }),
  z.object({
    accountSourceType: z.literal('SPECIFIC'),
    accountSourceIds: z.array(z.string().optional()),
  }),
]);

const amountSourceSchema = z.discriminatedUnion('amountMatchingOperator', [
  z.object({
    amountMatchingOperator: z.literal('BETWEEN'),
    amountType: amountTypeSchema,
    amount1: z.number().optional(),
    amount2: z.number(),
  }),
  z.object({
    amountMatchingOperator: z.enum(['EQUALS', 'GREATER_THAN', 'LESS_THAN', 'ANY_AMOUNT']),
    amountType: amountTypeSchema,
    amount1: z.number().optional(),
    // amount2 is not allowed in these cases
  }),
]);

const excludedTargetSchema = z
  .object({
    isExcludedFromReports: z.boolean(),
    isExcludedFromF2S: z.boolean(),
  })
  .partial();

export const RemoteTransactionRuleSchema = z
  .object({
    // entity
    id: z.string(),
    clientId: z.string(),
    createdAt: z.string(),
    modifiedAt: z.string(),
    hashKey: z.string(),
    // source
    payeeSourceCondition: payeeSourceSchema,
    amountSourceCondition: amountSourceSchema,
    coaSourceConditions: z.array(ChartOfAccountSchema),
    accountSourceCondition: accountSourceSchema,
    usageSourceType: usageTypeSchema,
    // target
    payeeTarget: z.string(),
    coaTargetCondition: ChartOfAccountSchema.optional(),
    businessCoaTargetCondition: ChartOfAccountSchema.optional(),
    useDefaultAccountUsage: z.boolean(),
    tagsTargetIds: z.array(z.string()),
    memoTarget: z.string(),
    isExcludedFromF2STarget: z.boolean(),
    isExcludedFromReportsTarget: z.boolean(),
    isReviewedTarget: z.boolean(),
    businessTargetId: z.string().optional(),
    // customerTargetId: z.string(),
    // projectTargetId: z.string(),
    // others
    applyToExistingTransactions: z.boolean(),
    clientIdFromEntityClientId: z.string(),
    isDeleted: z.boolean(),
    // linking
    prevTransactionRuleId: z.string(),
    nextTransactionRuleId: z.string(),
  })
  .partial();

export const TransactionRuleSchema = z
  .object({
    // entity
    id: z.string(),
    clientId: z.string(),
    createdAt: z.string(),
    modifiedAt: z.string(),
    hashKey: z.string(),
    // source
    payeeSource: payeeSourceSchema,
    amountSource: amountSourceSchema,
    coaSource: z.array(ChartOfAccountSchema),
    accountSource: accountSourceSchema,
    usageSource: usageTypeSchema,
    // target
    payeeTarget: z.string(),
    useDefaultAccountUsage: z.boolean(),
    coaTarget: ChartOfAccountSchema.optional(),
    businessCoaTarget: ChartOfAccountSchema.optional(),
    tagsTarget: z.array(z.string()),
    memoTarget: z.string(),
    excludedTarget: excludedTargetSchema,
    reviewedTarget: z.boolean(),
    businessTargetId: z.string().optional(),
    // customerTargetId: z.string(),
    // projectTargetId: z.string(),
    // others
    applyToExistingTransactions: z.boolean(),
    // linking
    prevTransactionRuleId: z.string(),
    nextTransactionRuleId: z.string(),
  })
  .partial();

export enum ConditionsEnum {
  PAYEE_SOURCE_CONDITION = 'payeeSource',
  AMOUNT_SOURCE_CONDITION = 'amountSource',
  COA_SOURCE_CONDITIONS = 'coaSource',
  ACCOUNT_SOURCE_CONDITIONS = 'accountSource',
  USAGE_SOURCE_TYPE = 'usageSource',
  PAYEE_TARGET = 'payeeTarget',
  COA_TARGET_CONDITION = 'coaTarget',
  BUSINESS_COA_TARGET_CONDITION = 'businessCoaTarget',
  TAGS_TARGET_IDS = 'tagsTarget',
  MEMO_TARGET = 'memoTarget',
  IS_EXCLUDED = 'excludedTarget',
  IS_REVIEWED_TARGET = 'reviewedTarget',
}

export const TransactionRuleConditionStatusSchema = z.object({
  payeeSource: z.boolean(),
  amountSource: z.boolean(),
  coaSource: z.boolean(),
  accountSource: z.boolean(),
  usageSource: z.boolean(),
  payeeTarget: z.boolean(),
  coaTarget: z.boolean(),
  businessCoaTarget: z.boolean(),
  tagsTarget: z.boolean(),
  memoTarget: z.boolean(),
  excludedTarget: z.boolean(),
  reviewedTarget: z.boolean(),
});

export type TransactionRuleConditionStatusType = z.infer<typeof TransactionRuleConditionStatusSchema>;
export type TransactionRuleConditionStatusTypeKeys = keyof z.infer<typeof TransactionRuleConditionStatusSchema>;
export type RemoteTransactionRuleType = z.infer<typeof RemoteTransactionRuleSchema>;
export type TransactionRuleType = z.infer<typeof TransactionRuleSchema>;
export type TransactionRuleEnabledOverrides = Partial<Omit<TransactionRuleConditionStatusType, 'businessCoaTarget'>>;
export type TransactionRuleFormInput = Partial<TransactionRuleType> & {
  enabledOverrides?: TransactionRuleEnabledOverrides;
};
export type PayeeSourceConditionType = z.infer<typeof payeeSourceSchema>;
export type AmountSourceConditionType = z.infer<typeof amountSourceSchema>;
export type AccountSourceConditionType = z.infer<typeof accountSourceSchema>;
export type ExcludedTragetConditionType = z.infer<typeof excludedTargetSchema>;
export type AmountSourceMatchingType = z.infer<typeof amountMatchingOperatorSchema>;
export type CategorySourceConditionsType = z.infer<typeof ChartOfAccountSchema>;
export type CategoryTargetConditionsType = z.infer<typeof ChartOfAccountSchema>;
export type UsageTypeSourceConditionsType = z.infer<typeof usageTypeSchema>;
export type PayeeNameType = z.infer<typeof payeeNameTypeSchema>;
export type PayeeMatchingType = z.infer<typeof payeeMatchingCriteriaSchema>;
export type PayeeMatchingOperatorType = z.infer<typeof payeeMatchingOperatorSchema>;
export type ServiceLevelType = z.infer<typeof serviceLevel>;
export type AmountType = z.infer<typeof amountTypeSchema>;
export type AccountType = z.infer<typeof accountTypeSchema>;

export type QCSAmountSourceConditionType =
  | {
      amountMatchingOperator: 'EQUALS' | 'GREATER_THAN' | 'LESS_THAN';
      amount1?: number;
    }
  | {
      amountMatchingOperator: 'BETWEEN';
      amount1?: number;
      amount2: number;
    };

export type QCSTransactionRuleRequestType = Omit<RemoteTransactionRuleType, 'amountSourceCondition'> & {
  amountSourceCondition?: QCSAmountSourceConditionType;
};

export type TransactionRulesState = QcsResourceSlice<TransactionRuleType>;
export enum ResolutionTypeEnum {
  CLOSE = 'close', // exit txn rule flow
  EDIT = 'editExistingRule', // conflict - edit existing
  REPLACE = 'replaceWithNewRule', // conflict - replace
  CREATE = 'createNewRule', // no conflict create
  UPDATE = 'updateExistingRule', // no conflict edit
}

// defaults
export const defaultPayeeSourceCondition: PayeeSourceConditionType = {
  payeeType: 'STATEMENT',
  payeeMatchingCriteria: [
    {
      payeeMatchingOperator: 'CONTAINS',
      payeeName: undefined,
    },
  ],
};

export const defaultAmountSourceCondition: AmountSourceConditionType = {
  amountType: 'EXPENSE',
  amountMatchingOperator: 'EQUALS',
  amount1: undefined,
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [TRANSACTION_RULES_REDUCER_KEY]: TransactionRulesState;
  }
}

export type RuleState = {
  serviceLevel: ServiceLevelType;
  stage?: TransactionRulesStages;

  buttonHelperText: string;
  buttonText: string;
  backText: string;
  titleText: string;

  rule?: TransactionRuleType;
  onUpdate: (rule?: TransactionRuleType) => void;
  duplicateId?: string;
  error?: string;
  fromTxn: boolean;
  editField?: TransactionRuleConditionStatusTypeKeys;
  inferredPayeeName?: string;
  statementPayeeName?: string;
  reviewCount: number;
  enabledOverrides?: Partial<TransactionRuleConditionStatusType>;
};

export type ContextType = {
  state: RuleState;
  dispatch: React.Dispatch<any>;
};

export type RuleProviderProps = {
  rule: TransactionRuleType;
  onUpdate: (rule?: TransactionRuleType) => void;
  children?: React.ReactNode;
  fromTxn?: boolean;
  editField?: TransactionRuleConditionStatusTypeKeys;
  inferredPayeeName?: string;
  statementPayeeName?: string;
  enabledOverrides?: Partial<TransactionRuleConditionStatusType>;
};

export type SubmitPayload = {
  transactionRule: TransactionRuleType;
  enabled: TransactionRuleConditionStatusType;
};

export type SubmitHandler<Payload, AdditionalArg = void> = (args: {
  payload: Payload;
  additionalArg: AdditionalArg;
}) => Promise<void> | void;

export const UPDATE_SERVICE_LEVEL = 'UPDATE_SERVICE_LEVEL';
export const UPDATE_STAGE = 'UPDATE_STAGE';
export const UPDATE_APPLY_TO_EXISTING = 'UPDATE_APPLY_TO_EXISTING';
export const UPDATE_RULE = 'UPDATE_RULE';
export const HANDLE_BACK_NAVIGATION = 'HANDLE_BACK_NAVIGATION';
export const UPDATE_DUPLICATE_ID = 'UPDATE_DUPLICATE_ID';
export const UPDATE_ERROR = 'UPDATE_ERROR';
export const UPDATE_FROM_TXN = 'UPDATE_FROM_TXN';

export type RuleAction = TaggedUnion<
  'type',
  {
    UPDATE_SERVICE_LEVEL: { payload: ServiceLevelType };
    UPDATE_STAGE: { payload: TransactionRulesStages };
    UPDATE_APPLY_TO_EXISTING: { payload: boolean };
    UPDATE_RULE: { payload: TransactionRuleType };
    HANDLE_BACK_NAVIGATION: { payload: TransactionRulesStages };
    UPDATE_DUPLICATE_ID: { payload: string };
    UPDATE_ERROR: { payload: string | undefined };
    UPDATE_FROM_TXN: { payload: boolean };
  }
>;

export const MAX_OR_CONDITIONS = 50;

export type TransactionRulePreviewResponse = AxiosResponse<TransactionRulePreviewType>;

export type TransactionRulePreviewType = {
  srcTxnsCount: number;
  resultTxnsCount: number;
  srcDaysHistory: number;
  previewMaxTxns: number;
  previewMaxDays: number;
  previewTxnsEndDate: string;
  transactionRule: TransactionRuleType;
  transactions: TransactionProps[];
};

export type TransactionRuleConditionUnion =
  | { condition: ConditionsEnum.PAYEE_SOURCE_CONDITION; value?: PayeeSourceConditionType }
  | { condition: ConditionsEnum.AMOUNT_SOURCE_CONDITION; value?: AmountSourceConditionType }
  | { condition: ConditionsEnum.COA_SOURCE_CONDITIONS; value?: CategorySourceConditionsType }
  | { condition: ConditionsEnum.ACCOUNT_SOURCE_CONDITIONS; value?: AccountSourceConditionType }
  | { condition: ConditionsEnum.USAGE_SOURCE_TYPE; value?: UsageTypeSourceConditionsType }
  | { condition: ConditionsEnum.PAYEE_TARGET; value?: PayeeNameType }
  | { condition: ConditionsEnum.COA_TARGET_CONDITION; value?: CategoryTargetConditionsType }
  | { condition: ConditionsEnum.BUSINESS_COA_TARGET_CONDITION; value?: CategoryTargetConditionsType }
  | { condition: ConditionsEnum.TAGS_TARGET_IDS; value?: string[] }
  | { condition: ConditionsEnum.MEMO_TARGET; value?: string }
  | { condition: ConditionsEnum.IS_EXCLUDED; value?: ExcludedTragetConditionType }
  | { condition: ConditionsEnum.IS_REVIEWED_TARGET; value?: boolean };
